/**
 * Family account types.
 *
 * This layer sits ABOVE the existing `Child` / `childStore` structure.
 * A `Family` is one parent account (device) with 1–8 `ChildProfile`s.
 * Each ChildProfile wraps the existing `Child` and adds per-child
 * preferences, privacy levels, and (derived) progress snapshots.
 *
 * Storage model:
 *   familyStore    — family meta + child ordering + active child
 *   childStore     — the authoritative Child[] (unchanged; `Family.childIds`
 *                    is just an ordered view over it)
 *   progressStore  — completions keyed by childId (already multi-child)
 *   other stores   — scoped by childId (already multi-child)
 *
 * The ChildProfile type is a READ-ONLY aggregate view — not a separate
 * persisted entity. It is assembled on demand via selectors so we never
 * double-write data already living in childStore / progressStore / etc.
 */

import type { AgeGroup, Child } from './child';
import type { CompletionRecord } from './progress';

/**
 * Hard caps on family size. Surfaced in UI (parent settings → add child).
 *
 * MAX_CHILDREN_PER_FAMILY is the absolute structural ceiling on the display
 * list (`Family.childIds`) and must stay >= the highest plan's seat count.
 * The Academy tier sells 8 seats (`TIER_LIMITS.academy.maxChildren`, marketed
 * as "Up to 8 children"), so this is 8. Per-plan gating on *adding* children
 * lives in `subscription.ts`; this constant just bounds what the switchers
 * (TopBar dropdown, FamilyStrip) can ever render.
 */
export const MIN_CHILDREN_PER_FAMILY = 1;
export const MAX_CHILDREN_PER_FAMILY = 8;

/**
 * Privacy level for a child's journal / portfolio entries.
 *
 * - `shared`   — visible to parent in the parent dashboard (default for
 *                pre-k, kinder). The child does not explicitly "publish";
 *                everything they make is co-owned.
 * - `parent-visible` — visible to parent but child sees a "Shared with
 *                grown-up" badge on entries (default for early-elementary).
 * - `private`  — only visible to the child by default; parent must
 *                explicitly request a review (default for upper-elementary
 *                who opt in). Matters for journaling / EQ entries.
 */
export type PrivacyLevel = 'shared' | 'parent-visible' | 'private';

/**
 * Per-child preferences — small, UI-facing toggles. Anything bigger
 * (mastery model, diagnostic outputs) lives in its own dedicated store
 * keyed by childId.
 */
export interface ChildPreferences {
  privacyLevel: PrivacyLevel;
  /** Parent-set: allow this child to appear in sibling co-op suggestions. */
  siblingCoopOptIn: boolean;
  /**
   * Role this child typically plays in co-op activities.
   * Defaults by age but parent can override (e.g. an eager 6-year-old
   * who likes to lead for a 4-year-old sibling).
   */
  preferredCoopRole: CoopRole;
  /** Accessibility preferences that need to persist per-child. */
  reducedMotion?: boolean;
  largerText?: boolean;
  /**
   * Parent-set: show faith-based (Christian) opt-in courses — e.g. Bible
   * Foundations — to this child. Default off; nothing faith-based surfaces
   * anywhere for this child until a parent turns it on in Manage Children.
   */
  faithContentOptIn?: boolean;
}

export type CoopRole = 'leader' | 'participant' | 'flexible';

/**
 * Aggregated, READ-ONLY view of a child. Assembled from childStore +
 * progressStore etc. via `selectChildProfile(childId)`. Components should
 * never try to write to a ChildProfile — route writes to the underlying
 * store.
 */
export interface ChildProfile {
  child: Child;
  preferences: ChildPreferences;
  /** Denormalised counts so the avatar row doesn't have to scan completions. */
  stats: {
    completionsLast7Days: number;
    currentStreak: number;
    xp: number;
    level: number;
  };
  /** Most recent 5 completions — enough for the profile switcher tooltip. */
  recentCompletions: CompletionRecord[];
}

/**
 * The Family record. One per device/account.
 *
 * `childIds` is the ORDERED display list for the avatar row. The source
 * of truth for Child data remains `childStore.children`. We keep the
 * ordering separate so reordering avatars doesn't require rewriting
 * every child record.
 */
export interface Family {
  id: string;
  /** Parent-chosen label for the family (e.g. "The Rivera Crew"). Optional. */
  label?: string;
  createdAt: string;
  /** Ordered list of childIds for display. Must be a subset of childStore.children. */
  childIds: string[];
  /** Which child is currently "driving" — shared with childStore.activeChildId. */
  activeChildId: string | null;
  /**
   * Shared-family mode: when true, the home screen renders ALL children's
   * feeds side-by-side instead of one active child. Parent toggles this
   * from the profile switcher.
   */
  sharedFamilyMode: boolean;
  /** Per-child preferences, keyed by childId. */
  preferencesByChild: Record<string, ChildPreferences>;
}

// ---------------------------------------------------------------------------
// Sibling co-op
// ---------------------------------------------------------------------------

/**
 * Co-op mode describes HOW two (or more) siblings participate together.
 *
 * - `leader-helper`   — older sibling teaches/leads; younger follows a
 *                       simplified version of the same activity.
 * - `parallel`        — each sibling does their own age variant side-by-side
 *                       and they compare results at the end.
 * - `team`            — both contribute to one shared outcome (e.g. build
 *                       one bridge, perform one play). Ages usually close.
 * - `observer`        — youngest watches & cheers; scored as half-credit.
 */
export type CoopMode = 'leader-helper' | 'parallel' | 'team' | 'observer';

/**
 * Sibling-coop metadata attached to (or derived for) an activity.
 *
 * This is a SEPARATE file from `types/activity.ts` to avoid churning the
 * existing Activity schema. Co-op info is layered on in two ways:
 *   1. Hand-authored for curated co-op activities (see
 *      `src/data/sibling-coop/*.json` — to be added in a follow-up).
 *   2. Heuristically derived at runtime from existing activities that are
 *      naturally sibling-friendly (see `deriveCoopHint()` in the store).
 */
export interface SiblingCoopActivity {
  activityId: string;
  /** `true` for activities explicitly designed/curated for multi-age groups. */
  siblingFriendly: true;
  /**
   * Inclusive min/max age gap in years that this co-op mode works for.
   * e.g. `{ min: 1, max: 4 }` — works with 1–4 year age gap between siblings.
   */
  ageGapYears: { min: number; max: number };
  /** Minimum age of the youngest participant (years). */
  youngestAgeMin: number;
  /** Maximum age of the oldest participant (years). Usually the activity cap. */
  oldestAgeMax: number;
  modes: CoopMode[];
  /**
   * Mode-specific instructions layered on top of the activity's ageVariants.
   * Parents see these in the co-op launcher.
   */
  modeNotes: Partial<Record<CoopMode, string>>;
  /** Recommended group size. Most are pair-only (2). A few scale to 3–4. */
  minParticipants: 2 | 3;
  maxParticipants: 2 | 3 | 4;
  /**
   * Target skills that co-op participation specifically develops — things
   * like `collaboration`, `teaching-others`. Distinct from the activity's
   * own `futureSkills`.
   */
  coopSkills: CoopSkill[];
}

export type CoopSkill =
  | 'collaboration'
  | 'teaching-others'
  | 'turn-taking'
  | 'conflict-resolution'
  | 'shared-authorship'
  | 'peer-feedback';

/**
 * A concrete co-op session — a launched activity with assigned sibling
 * roles. Persisted so completion credit flows to BOTH children.
 */
export interface CoopSession {
  id: string;
  activityId: string;
  mode: CoopMode;
  /** Ordered: index 0 is leader for `leader-helper`. */
  participants: Array<{
    childId: string;
    role: CoopRole;
    /** What age variant they actually ran (they can differ). */
    ageGroup: AgeGroup;
  }>;
  startedAt: string;
  completedAt?: string;
  /** Optional parent note after co-op. Feeds into next-time suggestions. */
  parentReflection?: string;
}
